import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CryptoWallet, CryptoToken } from "./types";
import { fetchCryptoWallet } from "./API/services";
import EmptyCryptoWalletState from "./EmptyCryptoWalletState";
import CryptoWalletCard from "./CryptoWalletCard";
import { useModal } from "../../hooks/useModal";
import CreateCryptoWalletModal from "./modals/CreateCryptoWalletModal";
import FundCryptoModal from "./modals/FundCryptoModal";
import WithdrawCryptoModal from "./modals/WithdrawCryptoModal";
import PageBreadCrumb from "../common/PageBreadCrumb";
import PageMeta from "../common/PageMeta";
import Spinner from "../ui/Spinner/Spinner";
import { RefreshIcon } from "../../icons";
import { History } from "lucide-react";

const TOKENS: CryptoToken[] = ["USDT", "USDC"];

const Crypto = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<CryptoWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeToken, setActiveToken] = useState<CryptoToken>("USDT");

  // ----- Modals
  const create = useModal();
  const fund = useModal();
  const withdraw = useModal();

  const loadWallet = async () => {
    try {
      setLoading(true);
      const data = await fetchCryptoWallet();
      setWallet(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallet();
  }, []);

  const balancesByToken = useMemo(() => {
    const map: Record<CryptoToken, typeof wallet extends null ? never : any[]> = {
      USDT: [],
      USDC: [],
    } as any;
    if (wallet) {
      for (const entry of wallet.balances) {
        map[entry.token]?.push(entry);
      }
    }
    return map;
  }, [wallet]);

  const handleFund = (token: CryptoToken) => {
    setActiveToken(token);
    fund.openModal();
  };

  const handleWithdraw = (token: CryptoToken) => {
    setActiveToken(token);
    withdraw.openModal();
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  const hasNoWallet = !wallet;

  return (
    <>
      <PageMeta
        title="Stable coin Wallet"
        description="Create a Stable coin Wallet to start receiving and sending cryptocurrency."
      />
      <PageBreadCrumb pageTitle="Stable coin Wallet" />

      <div className="p-6">
        {hasNoWallet ? (
          <EmptyCryptoWalletState onCreate={() => create.openModal()} />
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                {/* <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
                  Stable coin Wallet
                </h2> */}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Fund, withdraw and track your stable coin activity
                </p>
              </div>

              <div className="flex items-center gap-1">
                <button onClick={loadWallet} className="refresh-button">
                  <RefreshIcon className="w-7 h-7" />
                </button>
                <button
                  onClick={() => navigate("/stablecoin/transactions")}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <History className="h-4 w-4" />
                  Transactions
                </button>
              </div>
            </div>

            {/* One card per token, each with its own network breakdown */}
            <div className="grid gap-4 md:grid-cols-2">
              {TOKENS.map((token) => (
                <CryptoWalletCard
                  key={token}
                  token={token}
                  total={wallet.totalBalance[token] ?? 0}
                  networkBalances={balancesByToken[token] ?? []}
                  onFund={() => handleFund(token)}
                  onWithdraw={() => handleWithdraw(token)}
                />
              ))}
            </div>
          </>
        )}

        {/* ---- Create Wallet ---- */}
        <CreateCryptoWalletModal
          isOpen={create.isOpen}
          onClose={create.closeModal}
          onCreated={loadWallet}
        />

        {/* ---- Fund ---- */}
        <FundCryptoModal
          isOpen={fund.isOpen}
          onClose={fund.closeModal}
          wallet={wallet}
          token={activeToken}
        />

        {/* ---- Withdraw ---- */}
        <WithdrawCryptoModal
          isOpen={withdraw.isOpen}
          onClose={withdraw.closeModal}
          wallet={wallet}
          token={activeToken}
          onSuccess={loadWallet}
        />
      </div>
    </>
  );
};

export default Crypto;